import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/utils2";

const emptyCreate = { email: "", name: "", password: "", role: "operator" };

export default function Users() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null); // user object
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [resetPwd, setResetPwd] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!createForm.email.trim()) return toast.error("Email required");
    if (createForm.password.length < 8) return toast.error("Password must be at least 8 characters");
    try {
      await api.post("/users", { ...createForm, email: createForm.email.trim().toLowerCase() });
      toast.success("User created");
      setCreateForm(emptyCreate); setCreateOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const resetPassword = async () => {
    if (!resetTarget) return;
    if (resetPwd.length < 8) return toast.error("Password must be at least 8 characters");
    try {
      await api.patch(`/users/${resetTarget.id}/password`, { new_password: resetPwd });
      toast.success(`Password reset for ${resetTarget.email}`);
      setResetTarget(null); setResetPwd("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeUser = async (u) => {
    if (!confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-5xl mx-auto" data-testid="users-page">
      <PageHeader
        title="Users & Access"
        subtitle="Create data-entry accounts for your team. Operators can add and view entries but cannot delete anything — only admins can."
        action={
          <Button data-testid="add-user-btn"
            onClick={() => { setCreateForm(emptyCreate); setCreateOpen(true); }}
            className="rounded-sm bg-brand-forest hover:bg-brand-forestDark text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Add User
          </Button>
        }
      />

      <Card className="rounded-sm border-brand-line shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
            <tr>
              <th className="text-left px-5 py-3">Email</th>
              <th className="text-left px-3 py-3">Name</th>
              <th className="text-left px-3 py-3">Role</th>
              <th className="text-left px-3 py-3">Created</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-brand-muted">No users yet.</td></tr>
            )}
            {rows.map((u) => (
              <tr key={u.id} className="border-t border-brand-line hover:bg-secondary/40" data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-3 font-medium">{u.email}</td>
                <td className="px-3 py-3">{u.name || "—"}</td>
                <td className="px-3 py-3">
                  <Badge className={`rounded-sm text-[10px] uppercase tracking-wider ${
                    u.role === "admin"
                      ? "bg-brand-sand text-brand-ink border border-brand-sand"
                      : "bg-secondary text-brand-muted border border-brand-line"
                  }`} data-testid={`role-badge-${u.id}`}>
                    {u.role}
                  </Badge>
                </td>
                <td className="px-3 py-3 text-brand-muted text-xs">
                  {u.created_at ? formatDate(u.created_at.slice(0, 10)) : "—"}
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center gap-1 justify-end">
                    <Button variant="ghost" size="icon" className="h-7 w-7"
                      onClick={() => { setResetTarget(u); setResetPwd(""); }}
                      title="Reset password"
                      data-testid={`reset-pw-${u.id}`}
                    >
                      <KeyRound className="h-3.5 w-3.5" />
                    </Button>
                    {u.id !== me?.id && (
                      <Button variant="ghost" size="icon"
                        className="h-7 w-7 text-brand-terracotta hover:bg-brand-terracotta/10"
                        onClick={() => removeUser(u)}
                        title="Delete user"
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Create user dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Add User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email *</Label>
              <Input data-testid="new-user-email" type="email"
                className="mt-1.5 h-10 rounded-sm bg-white"
                value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="user@company.com" />
            </div>
            <div>
              <Label>Name</Label>
              <Input className="mt-1.5 h-10 rounded-sm bg-white"
                value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
            </div>
            <div>
              <Label>Temporary Password * <span className="text-brand-muted">(min 8 chars)</span></Label>
              <Input data-testid="new-user-password" type="text"
                className="mt-1.5 h-10 rounded-sm bg-white font-mono"
                value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} />
              <div className="text-[10px] text-brand-muted mt-1">Share this with the user. They can change it later from Settings → Change Password.</div>
            </div>
            <div>
              <Label>Role *</Label>
              <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                <SelectTrigger data-testid="new-user-role" className="mt-1.5 h-10 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="operator">Operator — Can add and view, cannot delete anything</SelectItem>
                  <SelectItem value="admin">Admin — Full access including deletions</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} data-testid="save-user-btn" className="rounded-sm bg-brand-forest text-white">Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(v) => !v && setResetTarget(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Reset Password</DialogTitle>
            <div className="text-xs text-brand-muted">Set a new password for <span className="font-medium">{resetTarget?.email}</span>.</div>
          </DialogHeader>
          <div>
            <Label>New Password (min 8 chars)</Label>
            <Input data-testid="reset-pw-input" type="text"
              className="mt-1.5 h-10 rounded-sm bg-white font-mono"
              value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={resetPassword} data-testid="save-reset-pw-btn" className="rounded-sm bg-brand-forest text-white">Update Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
